import { useMemo, useState } from 'react';
import type { StoreProfileDetail } from '../../types';
import type { SpatialEntity } from '../../lib/spatialEntity';
import {
  buildRetailerSummaries,
  type RetailerDestination,
} from '../../lib/retailerViewUtils';
import { RetailerStoreHeader } from './RetailerStoreHeader';
import { RetailerSummary } from './RetailerSummary';
import { RetailerNav } from './RetailerNav';
import { RetailerStoreMap } from './RetailerStoreMap';
import { CampaignsIssuesView } from './CampaignsIssuesView';
import { HistoryView } from './HistoryView';
import { StoreDetailsView } from './StoreDetailsView';

interface RetailerViewProps {
  profile: StoreProfileDetail;
  storeIdParam: string;
  onManageTwin: () => void;
}

export function RetailerView({ profile, storeIdParam, onManageTwin }: RetailerViewProps) {
  const [destination, setDestination] = useState<RetailerDestination>('details');
  const [selection, setSelection] = useState<{ kind: SpatialEntity['kind']; id: string } | null>(null);

  const asOf = null;
  const latestVisitSummary = useMemo(
    () => buildRetailerSummaries(profile, asOf).filter((summary) => summary.id === 'visit'),
    [profile],
  );
  const handleFocusLocation = (next: { kind: SpatialEntity['kind']; id: string }) => {
    setSelection(next);
    setDestination('map');
  };

  const handleFocusCampaign = (campaign: StoreProfileDetail['campaigns'][number]) => {
    if (campaign.floorplanZoneId) {
      setSelection({ kind: 'zone', id: campaign.floorplanZoneId });
      setDestination('map');
      return;
    }
    if (campaign.aisleId) {
      setSelection({ kind: 'aisle', id: campaign.aisleId });
      setDestination('map');
    }
  };

  const handleDestinationChange = (next: RetailerDestination) => {
    setDestination(next);
    requestAnimationFrame(() => {
      document.getElementById('retailer-destination-panel')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  return (
    <div className="space-y-4 max-w-[1600px] mx-auto">
      <RetailerStoreHeader
        profile={profile}
        storeIdParam={storeIdParam}
        onManageTwin={onManageTwin}
      />
      <RetailerSummary
        summaries={latestVisitSummary}
        onSummarySelect={() => setDestination('history')}
      />
      <RetailerNav active={destination} onChange={handleDestinationChange} />

      <div id="retailer-destination-panel" className="scroll-mt-24">
        {destination === 'map' && (
          <RetailerStoreMap
            profile={profile}
            asOf={asOf}
            selection={selection}
            onSelectionChange={setSelection}
          />
        )}

        {destination === 'campaigns' && (
          <CampaignsIssuesView
            profile={profile}
            asOf={asOf}
            onFocusLocation={handleFocusLocation}
            onFocusCampaign={handleFocusCampaign}
          />
        )}

        {destination === 'details' && <StoreDetailsView profile={profile} />}

        {destination === 'history' && <HistoryView profile={profile} />}
      </div>
    </div>
  );
}
